<template>
  <b-card body-class="mx-5">

    <b-row class="mb-5">
      <b-col>
        <b-img-lazy :src="$getConst.images.logo_wide" height="40" center/>
      </b-col>
    </b-row>

    <b-row class="text-uppercase mb-5">
      <b-col cols="6" class="text-left">
        <h5 class="text-dark font-weight-bold">
          {{$t($getConst.pages.invoice_details.invoice_id)}}:
          <b-skeleton width="5rem" class="mb-2 d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">
            {{invoice.vms_id}}
          </span>
        </h5>
        <h5 class="text-dark font-weight-bold">
          {{$t($getConst.pages.invoice_details.invoice_created_date)}}:
          <b-skeleton width="7rem" class="mb-2 d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">
            {{invoice.issued_date | localDate(true)}}
          </span>
        </h5>
        <h5 class="text-dark font-weight-bold">
          {{$t($getConst.pages.invoice_details.invoice_vendor_id)}}:
          <b-skeleton width="5rem" class="mb-2 d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">
            {{invoice.vendor_gp_id}}
          </span>
        </h5>
        <h5 class="text-dark font-weight-bold">
          {{$t($getConst.pages.invoice_details.invoice_vendor_name)}}:
          <b-skeleton width="10rem" class="mb-2 d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">
            {{invoice.vendor_name}}
          </span>
        </h5>
        <h5 class="text-dark font-weight-bold">
          {{$t($getConst.pages.invoice_details.invoice_worker_name)}}:
          <b-skeleton width="10rem" class="mb-2 d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="font-weight-light">
            {{invoice.worker_full_name}}
          </span>
        </h5>
      </b-col>
      <b-col cols="6" class="text-right">
        <h5 class="text-dark font-weight-bold">
          {{$t($getConst.pages.invoice_details.invoice_payment_status)}}:
          <b-skeleton width="7rem" class="mb-2 d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="text-secondary">
            {{invoice.payment_status}}
          </span>
        </h5>
        <h5 class="text-dark font-weight-bold">
          {{$t($getConst.pages.invoice_details.invoice_due_date)}}:
          <b-skeleton width="7rem" class="mb-2 d-inline-flex" v-if="isLoading"></b-skeleton>
          <span v-else class="text-danger">
            {{invoice.due_date | localDate(true)}}
          </span>
        </h5>
      </b-col>
    </b-row>

    <b-list-group class="text-capitalize">

      <!--   Timesheet details   -->
      <b-list-group-item class="list-group-section-title d-flex justify-content-between bg-light align-items-center">
        <div class="flex-grow-1">
          <h5 class="my-0 text-uppercase text-primary font-weight-bold">{{$t($getConst.pages.invoice_details.invoice_timesheet_section_title)}}</h5>
        </div>
        <AppSvgIcon height="2rem" :icon="this.$getConst.svgIcons.timesheets"/>
      </b-list-group-item>

      <b-list-group-item class="d-flex justify-content-between align-items-center font-size-lg">
        {{$t($getConst.pages.invoice_details.invoice_timesheet_id)}}
        <b-skeleton width="5rem" class="mb-2" v-if="isLoading"></b-skeleton>
        <span v-else>{{invoice.vms_timesheet_id}}</span>
      </b-list-group-item>
      <b-list-group-item class="d-flex justify-content-between align-items-center">
        {{$t($getConst.pages.invoice_details.invoice_timesheet_hours)}}
        <b-skeleton width="6rem" class="mb-2" v-if="isLoading"></b-skeleton>
        <span v-else>{{invoice.timesheet_hours}}</span>
      </b-list-group-item>
      <b-list-group-item class="d-flex justify-content-between align-items-center border-bottom-0">
        {{$t($getConst.pages.invoice_details.invoice_timesheet_end_date)}}
        <b-skeleton width="7rem" class="mb-2" v-if="isLoading"></b-skeleton>
        <span v-else>{{invoice.timesheet_end_date | localDate(true)}}</span>
      </b-list-group-item>

      <!--   Assignment details   -->
      <b-list-group-item class="list-group-section-title d-flex justify-content-between bg-light align-items-center">
        <div class="flex-grow-1">
          <h5 class="my-0 text-uppercase text-primary font-weight-bold">{{$t($getConst.pages.invoice_details.invoice_assignment_section_title)}}</h5>
        </div>
        <AppSvgIcon height="2rem" :icon="this.$getConst.svgIcons.job"/>
      </b-list-group-item>

      <b-list-group-item class="d-flex justify-content-between align-items-center font-size-lg">
        {{$t($getConst.pages.invoice_details.invoice_assignment_id)}}
        <b-skeleton width="5rem" class="mb-2" v-if="isLoading"></b-skeleton>
        <span v-else>{{invoice.vms_assignment_id}}</span>
      </b-list-group-item>
      <b-list-group-item class="d-flex justify-content-between align-items-center">
        {{$t($getConst.pages.invoice_details.invoice_assignment_bill_rate)}}
        <b-skeleton width="6rem" class="mb-2" v-if="isLoading"></b-skeleton>
        <span v-else>{{invoice.assignment_bill_rate}}</span>
      </b-list-group-item>
      <b-list-group-item class="d-flex justify-content-between align-items-center border-bottom-0">
        {{$t($getConst.pages.invoice_details.invoice_assignment_purchase_rate)}}
        <b-skeleton width="5rem" class="mb-2" v-if="isLoading"></b-skeleton>
        <span v-else class="">{{invoice.assignment_purchase_rate}}</span>
      </b-list-group-item>

      <!--   Payment details   -->
      <b-list-group-item class="list-group-section-title d-flex justify-content-between bg-light align-items-center">
        <div class="flex-grow-1">
          <h5 class="my-0 text-uppercase text-primary font-weight-bold">{{$t($getConst.pages.invoice_details.invoice_payment_section_title)}}</h5>
        </div>
        <AppSvgIcon height="2rem" :icon="this.$getConst.svgIcons.invoices"/>
      </b-list-group-item>

      <b-list-group-item class="d-flex justify-content-between align-items-center">
        {{$t($getConst.pages.invoice_details.invoice_payment_msp_fee)}}
        <b-skeleton width="5rem" class="mb-2" v-if="isLoading"></b-skeleton>
        <span v-else>{{invoice.msp_fee}} {{invoice.currency}}</span>
      </b-list-group-item>
      <b-list-group-item class="d-flex justify-content-between align-items-center">
        {{$t($getConst.pages.invoice_details.invoice_payment_vms_fee)}}
        <b-skeleton width="6rem" class="mb-2" v-if="isLoading"></b-skeleton>
        <span v-else>{{invoice.vms_fee}} {{invoice.currency}}</span>
      </b-list-group-item>
      <b-list-group-item class="d-flex justify-content-between align-items-center font-size-lg">
        {{$t($getConst.pages.invoice_details.invoice_payment_amount_supplier)}}
        <b-skeleton width="7rem" class="mb-2" v-if="isLoading"></b-skeleton>
        <span v-else>{{invoice.amount_supplier}} {{invoice.currency}}</span>
      </b-list-group-item>
      <b-list-group-item class="d-flex justify-content-between align-items-center border-bottom-0">
        {{$t($getConst.pages.invoice_details.invoice_payment_amount_client)}}
        <b-skeleton width="7rem" class="mb-2" v-if="isLoading"></b-skeleton>
        <span v-else>{{invoice.amount_client}} {{invoice.currency}}</span>
      </b-list-group-item>

    </b-list-group>
  </b-card>
</template>

<script>
  export default {
    name: "InvoiceDetails",
    props: {
      invoice: {
        required: true
      },
      isLoading: {
        type: Boolean,
        default: false
      }
    }
  }
</script>

<style scoped>
  .list-group {
    border-radius: 0 !important;
  }
  .list-group-item {
    border-right: none !important;
    border-left: none !important;
  }
  .list-group-section-title {
    border-top: none !important;
  }
  .b-skeleton {
    height: 20px;
  }
</style>
